// src/modules/hrm/tasks/components/TaskRegisterModal.jsx
import React, { useState, useEffect } from "react";
import {
  X,
  Plus,
  Trash2,
  ClipboardList,
  Calendar,
  Tag,
  Loader2,
} from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import {
  showSuccessToast,
  showErrorToast,
  showConfirmDialog,
} from "../../../../components/ConfirmDialog";
import { createTask } from "../../../../api/hrm/tasks/task_info";
import { getTaskTags } from "../../../../api/hrm/tasks/task_tag_info";
import Select from "react-select";
import customSelectStyles from "../../../../components/ui/customSelectStyles";
import dayjs from "../../../../utils/dayjs";

const labelClass =
  "text-[11px] font-bold text-gray-400 uppercase tracking-widest ml-1";
const inputClass =
  "w-full bg-white border border-gray-300 rounded-2xl px-4 py-3 text-sm font-medium text-gray-800 outline-none transition-all hover:border-gray-400 focus:bg-white focus:border-red-500 focus:ring-4 focus:ring-red-500/5";

const getNextWorkingDate = () => {
  const today = dayjs().tz("Asia/Ho_Chi_Minh");
  const day = today.day();
  if (day === 6) return today.add(2, "day");
  if (day === 0) return today.add(1, "day");
  return today.add(1, "day");
};

const selectStyles = {
  ...customSelectStyles,
  control: (provided, state) => ({
    ...(typeof customSelectStyles.control === "function"
      ? customSelectStyles.control(provided, state)
      : provided),
    minHeight: 48,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: state.isFocused ? "#ef4444" : "#d1d5db",
    backgroundColor: "#fff",
    boxShadow: state.isFocused ? "0 0 0 4px rgba(239, 68, 68, 0.05)" : "none",
    "&:hover": {
      borderColor: state.isFocused ? "#ef4444" : "#9ca3af",
    },
  }),
  menuPortal: (base) => ({ ...base, zIndex: 999999 }),
};

const TaskRegisterDialog = ({ isOpen, onClose, onSaved }) => {
  const currentUser = JSON.parse(localStorage.getItem("user") || "{}");

  const [taskInput, setTaskInput] = useState("");
  const [noteInput, setNoteInput] = useState("");
  const [taskList, setTaskList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);
  const [tagList, setTagList] = useState([]);
  const nextWorkingDate = getNextWorkingDate().format("YYYY-MM-DD");
  const [workDate, setWorkDate] = useState(nextWorkingDate);

  useEffect(() => {
    if (!isOpen) return;
    async function loadTags() {
      try {
        const res = await getTaskTags();
        setTagList(res || []);
      } catch (err) {
        console.error(err);
        setTagList([]);
      }
    }
    loadTags();
  }, [isOpen]);

  const handleAddTask = () => {
    if (!taskInput.trim()) {
      showErrorToast("Vui lòng nhập tên công việc");
      return;
    }
    const newTask = {
      taskname: taskInput.trim(),
      note: noteInput.trim(),
      work_day: workDate,
      tag_ids: selectedTags.map((t) => t.value),
      tag_labels: selectedTags.map((t) => t.label),
    };
    setTaskList([...taskList, newTask]);
    setTaskInput("");
    setNoteInput("");
  };

  const handleRemoveTask = (index) => {
    showConfirmDialog({
      title: "Xác nhận xóa",
      text: "Bạn có chắc muốn xóa công việc này?",
      confirmText: "Đồng ý",
      cancelText: "Hủy",
    }).then((res) => {
      if (res.isConfirmed) {
        const updated = [...taskList];
        updated.splice(index, 1);
        setTaskList(updated);
      }
    });
  };

  const handleSave = async () => {
    if (taskList.length === 0) {
      showErrorToast("Chưa có công việc nào để lưu!");
      return;
    }

    try {
      setLoading(true);
      await Promise.all(
        taskList.map((t) =>
          createTask({
            ...t,
            tag_ids: t.tag_ids,
            employee_id: currentUser.id,
            branch_id: currentUser.branch?.id || null,
            department_id:
              currentUser.department_id || currentUser.department?.id || null,
          })
        )
      );

      showSuccessToast("Đã đăng ký công việc thành công!");
      setTaskList([]);
      setWorkDate(nextWorkingDate);
      setSelectedTags([]);
      onSaved?.();
      onClose();
    } catch (err) {
      console.error("❌ Lỗi lưu công việc:", err);
      showErrorToast("Không thể lưu danh sách công việc");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[200] bg-black/45 backdrop-blur-sm"
            onClick={onClose}
          />

          <div className="fixed inset-0 z-[210] flex items-end justify-center p-0 pointer-events-none sm:items-center sm:p-4">
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 30, stiffness: 320 }}
              className="pointer-events-auto flex h-[90dvh] w-full max-w-3xl flex-col overflow-hidden rounded-t-[1.75rem] border border-gray-200 bg-white shadow-2xl sm:h-auto sm:max-h-[90vh] sm:rounded-[2rem]"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex shrink-0 items-start justify-between gap-3 border-b border-gray-200 bg-gradient-to-r from-red-50/70 via-white to-white px-5 py-4 sm:px-6">
                <div className="flex min-w-0 items-center gap-3 sm:gap-4">
                  <div className="rounded-2xl border border-red-700/10 bg-red-700/5 p-3 text-red-700 shadow-sm">
                    <ClipboardList className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={2.25} />
                  </div>
                  <div className="min-w-0">
                    <h2 className="text-lg font-black uppercase tracking-tight text-gray-800 sm:text-xl">
                      Đăng ký <span className="text-red-700">công việc</span>
                    </h2>
                    <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.16em] text-gray-400">
                      Lên danh sách việc cho ngày làm việc sắp tới
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-xl border border-gray-200 bg-white p-2 text-gray-500 transition hover:border-red-200 hover:bg-red-50 hover:text-red-600"
                  aria-label="Đóng"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Body */}
              <div className="min-h-0 flex-1 space-y-5 overflow-y-auto overscroll-contain px-5 py-5 sm:px-6">
                <section className="space-y-4 rounded-2xl border border-gray-200 bg-gradient-to-br from-gray-50/80 to-white p-4 sm:p-5">
                  <h3 className="text-[11px] font-black uppercase tracking-widest text-gray-500">
                    Thông tin đăng ký
                  </h3>

                  <div className="flex flex-col gap-4 md:flex-row md:items-start">
                    <div className="w-full md:w-44 md:shrink-0">
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Calendar size={12} className="text-red-500" />
                        Ngày thực hiện
                      </label>
                      <input
                        type="date"
                        className={inputClass}
                        value={workDate}
                        min={nextWorkingDate}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          if (dayjs(selectedDate).day() === 0) {
                            showErrorToast("Chủ nhật không phải ngày làm việc");
                            setWorkDate(nextWorkingDate);
                            return;
                          }
                          if (dayjs(selectedDate).isBefore(dayjs(nextWorkingDate))) {
                            showErrorToast("Không thể chọn ngày trong quá khứ");
                            setWorkDate(nextWorkingDate);
                            return;
                          }
                          setWorkDate(selectedDate);
                        }}
                      />
                    </div>

                    <div className="min-w-0 flex-1">
                      <label className={`${labelClass} mb-1.5 flex items-center gap-1.5`}>
                        <Tag size={12} className="text-red-500" />
                        Phân loại (Tag)
                      </label>
                      <Select
                        isMulti
                        styles={selectStyles}
                        options={tagList.map((tag) => ({
                          value: tag.id,
                          label: tag.tag_name,
                          color: tag.color,
                        }))}
                        value={selectedTags}
                        onChange={(selected) => {
                          if (!selected) return setSelectedTags([]);
                          if (selected.length > 3) {
                            showErrorToast("Chỉ được chọn tối đa 3 tags!");
                            return;
                          }
                          setSelectedTags(selected);
                        }}
                        placeholder="Tìm hoặc chọn tag..."
                        menuPortalTarget={document.body}
                      />
                      <p className="mt-1 text-[11px] text-gray-400">Tối đa 3 nhãn</p>
                    </div>
                  </div>

                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>
                      Tên công việc <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder="VD: Chuẩn bị tài liệu đào tạo"
                      value={taskInput}
                      onChange={(e) => setTaskInput(e.target.value)}
                      maxLength={100}
                      className={inputClass}
                    />
                    <p className="mt-1 text-right text-[11px] text-gray-400">
                      {taskInput.length}/100
                    </p>
                  </div>

                  <div>
                    <label className={`${labelClass} mb-1.5 block`}>Ghi chú</label>
                    <textarea
                      rows={3}
                      placeholder="Thêm mô tả chi tiết nếu có..."
                      value={noteInput}
                      onChange={(e) => setNoteInput(e.target.value)}
                      maxLength={250}
                      className={`${inputClass} resize-none`}
                    />
                    <p
                      className={`mt-1 text-right text-[11px] ${
                        noteInput.length > 200 ? "font-bold text-red-500" : "text-gray-400"
                      }`}
                    >
                      {noteInput.length}/250
                    </p>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={handleAddTask}
                      className="inline-flex items-center gap-2 rounded-xl border-2 border-red-600 bg-white px-4 py-2.5 text-xs font-black uppercase tracking-wide text-red-700 shadow-sm transition hover:bg-red-50 active:scale-[0.98]"
                    >
                      <Plus size={16} strokeWidth={2.5} />
                      Thêm vào danh sách
                    </button>
                  </div>
                </section>

                <section>
                  <div className="mb-3 flex items-center gap-2">
                    <h3 className="text-sm font-black uppercase tracking-wide text-gray-700">
                      Danh sách đăng ký
                    </h3>
                    <span className="rounded-full border border-red-100 bg-red-50 px-2 py-0.5 text-[11px] font-black text-red-600">
                      {taskList.length}
                    </span>
                  </div>

                  {taskList.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/60 px-4 py-10 text-center">
                      <ClipboardList className="mx-auto mb-2 h-8 w-8 text-gray-300" />
                      <p className="text-sm font-medium text-gray-400">
                        Chưa có công việc nào được thêm
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2.5">
                      {taskList.map((t, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-3 rounded-2xl border border-gray-200 bg-white p-3.5 shadow-sm transition hover:border-red-300"
                        >
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-red-50 text-xs font-black text-red-600">
                            {String(idx + 1).padStart(2, "0")}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="mb-1 flex flex-wrap items-center gap-2">
                              <span className="rounded-lg bg-slate-100 px-2 py-0.5 text-[10px] font-black text-slate-600">
                                {dayjs(t.work_day).format("DD/MM/YYYY")}
                              </span>
                              {t.tag_labels?.map((name) => (
                                <span
                                  key={name}
                                  className="rounded-full border border-gray-200 bg-gray-50 px-2 py-0.5 text-[10px] font-bold text-gray-500"
                                >
                                  {name}
                                </span>
                              ))}
                            </div>
                            <p className="truncate text-sm font-bold text-gray-800">{t.taskname}</p>
                            {t.note && (
                              <p className="mt-0.5 line-clamp-2 text-xs italic text-gray-400">
                                {t.note}
                              </p>
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveTask(idx)}
                            title="Xóa"
                            className="rounded-xl border border-gray-200 p-2 text-gray-400 transition hover:border-red-300 hover:bg-red-50 hover:text-red-600"
                          >
                            <Trash2 size={15} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              </div>

              {/* Footer */}
              <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-5 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-6">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border border-gray-200 bg-white px-6 py-2.5 text-xs font-bold uppercase tracking-wider text-gray-600 transition hover:border-gray-300 hover:bg-gray-50"
                >
                  Hủy
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={loading || taskList.length === 0}
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-red-700 to-red-800 px-6 py-2.5 text-xs font-black uppercase tracking-wider text-white shadow-md shadow-red-100 transition hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang lưu...
                    </>
                  ) : (
                    `Lưu ${taskList.length > 0 ? `(${taskList.length})` : ""} công việc`
                  )}
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TaskRegisterDialog;
